import { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Grid3X3, Plus, Pencil, Trash2, X, Save, Upload, Image, GripVertical, Check
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, data: null, isNew: false });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditModal(prev => ({
        ...prev,
        data: { ...prev.data, image_url: response.data.url }
      }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Fehler beim Hochladen des Bildes');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!editModal.data?.name) {
      alert('Bitte gib einen Namen ein');
      return;
    }

    setSaving(true);
    try {
      if (editModal.isNew) {
        await axios.post(`${API}/admin/categories`, editModal.data);
      } else {
        await axios.put(`${API}/admin/categories/${editModal.data.id}`, editModal.data);
      }
      setEditModal({ open: false, data: null, isNew: false });
      fetchCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      alert(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Kategorie wirklich löschen? Dies kann nicht rückgängig gemacht werden.')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      fetchCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openNewCategory = () => {
    setEditModal({
      open: true,
      isNew: true,
      data: {
        name: '',
        subtitle: '',
        image_url: '',
        benefits: ['', '', ''],
        order: categories.length
      }
    });
  };

  const openEditCategory = (category) => {
    setEditModal({
      open: true,
      isNew: false,
      data: {
        ...category,
        benefits: category.benefits?.length === 3 ? category.benefits : ['', '', '']
      }
    });
  };

  const updateBenefit = (index, value) => {
    const newBenefits = [...(editModal.data.benefits || ['', '', ''])];
    newBenefits[index] = value;
    setEditModal(prev => ({
      ...prev,
      data: { ...prev.data, benefits: newBenefits }
    }));
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-lilac/50 rounded-none w-1/3"></div>
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-48 bg-lilac/50 rounded-none"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-categories">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-ink">Kategorien verwalten</h1>
          <p className="text-ink/80 mt-1">
            Verwalte die Kategorien der digitalen Gastro-Landschaft.
          </p>
        </div>
        <Button onClick={openNewCategory} className="">
          <Plus className="w-4 h-4 mr-2" />
          Neue Kategorie
        </Button>
      </div>

      {/* Categories Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category.id} className="bg-white rounded-none border-2 border-ink overflow-hidden group">
            {/* Image */}
            <div className="relative h-40 bg-off">
              {category.image_url ? (
                <img 
                  src={category.image_url} 
                  alt={category.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-12 h-12 text-ink/60" />
                </div>
              )}
              <div className="absolute inset-0 bg-ink/60" />
              <div className="absolute bottom-3 left-4 right-4">
                <h3 className="font-bold text-white text-lg">{category.name}</h3>
                {category.subtitle && (
                  <p className="text-white/80 text-sm">{category.subtitle}</p>
                )}
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {/* Benefits */}
              {category.benefits?.length > 0 && (
                <div className="space-y-1 mb-4">
                  {category.benefits.filter(b => b).map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-ink/80">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t border-ink/15">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditCategory(category)}
                  className="flex-1"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Bearbeiten
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteCategory(category.id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        
        {categories.length === 0 && (
          <div className="col-span-full text-center py-12 bg-white rounded-none border-2 border-ink">
            <Grid3X3 className="w-12 h-12 text-ink/60 mx-auto mb-3" />
            <p className="text-ink/60">Noch keine Kategorien vorhanden</p>
            <Button onClick={openNewCategory} variant="outline" className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Erste Kategorie erstellen
            </Button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-ink">
                {editModal.isNew ? 'Neue Kategorie' : 'Kategorie bearbeiten'}
              </h2>
              <button onClick={() => setEditModal({ open: false, data: null, isNew: false })} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              {/* Image Upload */}
              <div>
                <Label className="text-ink/80 font-medium mb-2 block">Bild</Label>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-ink/30 rounded-none p-4 cursor-pointer hover:border-purple transition-colors"
                >
                  {editModal.data?.image_url ? (
                    <div className="relative">
                      <img 
                        src={editModal.data.image_url} 
                        alt="Preview" 
                        className="w-full h-32 object-cover rounded-none"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditModal(prev => ({ ...prev, data: { ...prev.data, image_url: '' } }));
                        }}
                        className="absolute top-2 right-2 p-1 bg-white rounded-none"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      {uploadingImage ? (
                        <p className="text-ink/60">Wird hochgeladen...</p>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-ink/60 mx-auto mb-2" />
                          <p className="text-sm text-ink/60">Klicken zum Hochladen</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Name */}
              <div>
                <Label className="text-ink/80 font-medium mb-2 block">Name *</Label>
                <Input
                  value={editModal.data?.name || ''}
                  onChange={(e) => setEditModal(prev => ({ ...prev, data: { ...prev.data, name: e.target.value } }))}
                  placeholder="z.B. Kassensystem"
                />
              </div>

              {/* Subtitle */}
              <div>
                <Label className="text-ink/80 font-medium mb-2 block">Untertitel</Label>
                <Input
                  value={editModal.data?.subtitle || ''}
                  onChange={(e) => setEditModal(prev => ({ ...prev, data: { ...prev.data, subtitle: e.target.value } }))}
                  placeholder="z.B. Moderne POS-Lösungen"
                />
              </div>

              {/* Benefits */}
              <div>
                <Label className="text-ink/80 font-medium mb-2 block">Vorteile (3 Haken)</Label>
                <div className="space-y-2">
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <Input
                        value={editModal.data?.benefits?.[index] || ''}
                        onChange={(e) => updateBenefit(index, e.target.value)}
                        placeholder={`Vorteil ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button variant="outline" onClick={() => setEditModal({ open: false, data: null, isNew: false })} className="flex-1">
                Abbrechen
              </Button>
              <Button
                onClick={handleSaveCategory}
                disabled={saving}
                className="flex-1"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
